import { useRef, useState } from "react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { ChapterLabel } from "./Reveal";
import { JOURNEY_STAGES } from "@/data";

export default function Journey() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(JOURNEY_STAGES.length - 1, Math.floor(v * JOURNEY_STAGES.length)));
  });

  const stage = JOURNEY_STAGES[active];

  return (
    <section ref={ref} data-testid="journey-section" className="relative h-[340vh]">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-12">
          <ChapterLabel number="04" label="The Learning Experience" />
          <h2
            data-testid="journey-heading"
            className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl leading-[1.15] tracking-tight text-[#FFFDF9]"
          >
            From explanation to{" "}
            <span className="italic text-[#38BDF8]">understanding</span>.
          </h2>
          <p className="mt-3 text-sm text-slate-400">
            A simple learning journey, enhanced by AuraXR.
          </p>

          <div className="mt-8 grid items-center gap-8 lg:grid-cols-[0.85fr_1.6fr]">
            <div className="relative order-2 lg:order-1 min-h-[170px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={stage.num}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span
                    data-testid="journey-stage-number"
                    className="font-display italic text-6xl sm:text-7xl text-[#38BDF8]/25"
                  >
                    {stage.num}
                  </span>
                  <h3
                    data-testid="journey-stage-title"
                    className="mt-1 font-display text-2xl sm:text-3xl uppercase tracking-[0.12em] text-[#FFFDF9]"
                  >
                    {stage.title}
                  </h3>
                  <p
                    data-testid="journey-stage-desc"
                    className="mt-3 max-w-sm text-sm text-slate-300 leading-relaxed"
                  >
                    {stage.desc}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="mt-8 flex items-center gap-3" data-testid="journey-progress">
                {JOURNEY_STAGES.map((s, i) => (
                  <div key={s.num} className="flex items-center gap-3">
                    <span
                      className={`font-display text-sm transition-colors duration-500 ${
                        i === active ? "text-[#38BDF8]" : "text-slate-600"
                      }`}
                    >
                      {s.num}
                    </span>
                    {i < JOURNEY_STAGES.length - 1 && (
                      <span
                        className={`h-px w-7 transition-colors duration-500 ${
                          i < active ? "bg-[#38BDF8]/70" : "bg-slate-700"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-1 lg:order-2 aspect-[16/10] max-h-[62vh] w-full overflow-hidden rounded-2xl border border-white/[0.08]">
              {JOURNEY_STAGES.map((s, i) => (
                <img
                  key={s.num}
                  src={s.img}
                  alt={`${s.title} — ${s.desc}`}
                  draggable={false}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                    i === active ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050811]/50 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
